/*
  KA motion — scroll entrances and ambient/idle motion.

  One IntersectionObserver for the whole page rather than one per section.
  Twelve ka-* sections already ship their own observer; this does not replace
  them yet (see status.md), it covers the sections that had nothing and gives
  the new work a single vocabulary to share with ka-collection-intro.js.

  The `ka-motion` class on <html> is what arms the hidden-until-seen CSS. It is
  set here, synchronously at parse time, and only when motion is actually
  wanted — so no-JS, reduced-motion and theme-editor visitors get plain,
  always-visible content instead of a page that never reveals itself.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  if (reduced || inDesignMode || !('IntersectionObserver' in window)) return;

  // The `ka-motion` class is set by an inline script in layout/theme.liquid,
  // before first paint -- setting it from here (deferred) applies opacity:0 as a
  // *change* rather than an initial value, which animates the hide and makes the
  // page fade out on load. This line is a no-op safety net for any document that
  // renders without that head script; adding an existing class costs nothing.
  document.documentElement.classList.add('ka-motion');

  // `ka-prop` = this engine can register and therefore ANIMATE a custom
  // property (@property). The glimmer ring rotates a conic-gradient by
  // animating an <angle> custom property, which is inert without it — an
  // unregistered custom property is not interpolable, so the ring would sit
  // frozen at 0deg rather than degrade to nothing. ka-motion.css keys a
  // linear-sweep fallback off the absence of this class. CSSPropertyRule is
  // the standard detection: Chrome 85+, Safari 16.4+, Firefox 128+.
  if (window.CSSPropertyRule) document.documentElement.classList.add('ka-prop');

  /** Stagger index is assigned per group so siblings cascade, not the whole page. */
  const indexGroup = (root) => {
    const groups = new Map();

    root.querySelectorAll('[data-ka-anim]').forEach((el) => {
      if (el.dataset.kaAnimIndexed === 'true') return;
      el.dataset.kaAnimIndexed = 'true';

      const key = el.closest('[data-ka-anim-group]') || el.parentElement || document.body;
      const next = groups.get(key) ?? 0;
      groups.set(key, next + 1);
      el.style.setProperty('--ka-anim-i', String(Math.min(next, 8)));
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          // Ambient loops pause off-screen; entrances are one-shot and stay put.
          if (entry.target.hasAttribute('data-ka-ambient')) {
            entry.target.classList.remove('is-in');
          }
          continue;
        }

        const el = entry.target;

        /*
          PDP/collection arrival staging (ka-collection-intro.js) hides a
          whole [data-ka-intro-step] group behind opacity:0 while it waits
          to slide in -- but that leaves its layout box in place, so any
          [data-ka-anim] descendant (gallery thumbnails, buy buttons, ...)
          is still geometrically in the viewport and fires here almost
          immediately, invisibly finishing its own entrance transition
          behind the still-hidden group. By the time the group itself
          reveals there is nothing left to cascade. Defer to the group's
          own reveal instead -- ka-collection-intro.js's revealGroup() walks
          these same descendants once the group actually lands, calling
          window.kaMotion.reveal() on each. This entry is simply skipped,
          not requeued, since the element never stops intersecting in
          between and the observer only reports a change once.
        */
        const stagedGroup = el.hasAttribute('data-ka-anim')
          ? el.closest('[data-ka-intro-step]')
          : null;
        if (
          stagedGroup &&
          document.documentElement.classList.contains('ka-intro-armed') &&
          !stagedGroup.classList.contains('is-in')
        ) {
          continue;
        }

        el.classList.add('is-in');

        if (el.hasAttribute('data-ka-anim')) {
          // Stop observing entrances once they have played — one-shot per visit.
          if (!el.hasAttribute('data-ka-ambient')) observer.unobserve(el);
          el.addEventListener(
            'transitionend',
            () => el.classList.add('is-settled'),
            { once: true }
          );
        }
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
  );

  const scan = (root = document) => {
    indexGroup(root);
    root.querySelectorAll('[data-ka-anim], [data-ka-ambient]').forEach((el) => observer.observe(el));
  };

  scan();

  // Sections re-render in the theme editor and on section-rendering swaps.
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target instanceof Element) scan(target);
  });

  /*
    Content injected after load has to be picked up too.

    The recommendations grid swaps its own innerHTML in once the fetch resolves,
    which fires no shopify:section:load — so its cards kept the hidden state
    forever: correct size, in the viewport, images decoded, opacity 0. Anything
    else that injects markup later (quick-add, cart drawer, section rendering)
    would hit exactly the same wall, so this is fixed here rather than by
    dropping the entrance hook from that one grid.

    Only added nodes are inspected, and only when they actually contain an
    entrance hook, so the common case costs a cheap querySelector on a subtree
    that just changed anyway.
  */
  const injected = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        const hooks = [
          ...(node.matches('[data-ka-anim]') ? [node] : []),
          ...node.querySelectorAll('[data-ka-anim]'),
        ];
        if (!hooks.length) continue;

        /*
          Revealed outright rather than handed to the IntersectionObserver.

          Content injected after load arrives precisely because the visitor is
          already at it -- the recommendations grid fetches on approach -- so
          there is no entrance left to wait for. More importantly it removes a
          way for the page to fail closed: if the observer never fires (a
          background tab, a throttled renderer, any edge case), observed
          elements sit at opacity 0 indefinitely. On initial markup that risk is
          bounded, because the observer reports intersecting elements as soon as
          it starts. On injected markup it is not worth taking at all -- an
          invisible product grid on a shop page is a sale lost to a decoration.
        */
        indexGroup(node.parentElement || node);
        hooks.forEach((el) => el.classList.add('is-in', 'is-settled'));

        node.querySelectorAll('[data-ka-ambient]').forEach((el) => observer.observe(el));
      }
    }
  });

  injected.observe(document.body, { childList: true, subtree: true });

  // Exposed so ka-collection-intro.js can reveal a staged group directly
  // instead of waiting for the observer to notice it.
  window.kaMotion = {
    reveal(el) {
      if (!el) return;
      el.classList.add('is-in');
      observer.unobserve(el);
    },
    scan,
  };
})();
